import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

interface EnterpriseContactFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EnterpriseContactForm({ open, onOpenChange }: EnterpriseContactFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    contactName: "",
    email: "",
    phone: "",
    companyName: "",
    companyAddress: "",
    numberOfVehicles: "",
    numberOfDrivers: "",
    numberOfDepots: "",
    additionalInfo: "",
  });

  const update = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.contactName || !form.email || !form.companyName || !form.numberOfVehicles) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-enterprise-enquiry", {
        body: form,
      });

      if (error) throw error;
      setSubmitted(true);
    } catch (err: any) {
      console.error("Enterprise enquiry error:", err);
      toast.error("Failed to send enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset after close animation
    setTimeout(() => {
      setSubmitted(false);
      setForm({
        contactName: "",
        email: "",
        phone: "",
        companyName: "",
        companyAddress: "",
        numberOfVehicles: "",
        numberOfDrivers: "",
        numberOfDepots: "",
        additionalInfo: "",
      });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[90dvh] overflow-y-auto">
        {submitted ? (
          <div className="py-8 text-center space-y-4">
            <CheckCircle2 className="h-16 w-16 text-primary mx-auto" />
            <DialogTitle className="text-2xl font-bold">Thank You!</DialogTitle>
            <p className="text-muted-foreground max-w-sm mx-auto">
              We've received your enterprise enquiry. A member of our team will be in touch within <strong>2 working days</strong>.
            </p>
            <Button onClick={handleClose} className="mt-4">
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Enterprise Enquiry</DialogTitle>
              <DialogDescription>
                Tell us about your operation and we'll put together a custom package for your fleet.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              {/* Contact Details */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Contact Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="contactName">Full Name *</Label>
                    <Input
                      id="contactName"
                      placeholder="John Smith"
                      value={form.contactName}
                      onChange={(e) => update("contactName", e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@company.co.uk"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="07xxx xxxxxx"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                  />
                </div>
              </div>

              {/* Company Details */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Company Details</h4>
                <div>
                  <Label htmlFor="companyName">Company Name *</Label>
                  <Input
                    id="companyName"
                    placeholder="ABC Logistics Ltd"
                    value={form.companyName}
                    onChange={(e) => update("companyName", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="companyAddress">Company Address</Label>
                  <Input
                    id="companyAddress"
                    placeholder="123 High Street, London"
                    value={form.companyAddress}
                    onChange={(e) => update("companyAddress", e.target.value)}
                  />
                </div>
              </div>

              {/* Fleet Details */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Fleet Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="numberOfVehicles">Vehicles *</Label>
                    <Select
                      value={form.numberOfVehicles}
                      onValueChange={(v) => update("numberOfVehicles", v)}
                    >
                      <SelectTrigger id="numberOfVehicles">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15-25">15–25</SelectItem>
                        <SelectItem value="26-50">26–50</SelectItem>
                        <SelectItem value="51-100">51–100</SelectItem>
                        <SelectItem value="100+">100+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="numberOfDrivers">Drivers</Label>
                    <Select
                      value={form.numberOfDrivers}
                      onValueChange={(v) => update("numberOfDrivers", v)}
                    >
                      <SelectTrigger id="numberOfDrivers">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1–10</SelectItem>
                        <SelectItem value="11-25">11–25</SelectItem>
                        <SelectItem value="26-50">26–50</SelectItem>
                        <SelectItem value="50+">50+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="numberOfDepots">Depots/Sites</Label>
                    <Select
                      value={form.numberOfDepots}
                      onValueChange={(v) => update("numberOfDepots", v)}
                    >
                      <SelectTrigger id="numberOfDepots">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2-5">2–5</SelectItem>
                        <SelectItem value="6-10">6–10</SelectItem>
                        <SelectItem value="10+">10+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div>
                <Label htmlFor="additionalInfo">Anything else we should know?</Label>
                <Textarea
                  id="additionalInfo"
                  placeholder="Tell us about your current setup, pain points, or specific requirements..."
                  value={form.additionalInfo}
                  onChange={(e) => update("additionalInfo", e.target.value)}
                  rows={3}
                />
              </div>

              <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Submit Enquiry"
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
